// @ts-nocheck
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowRight, Eye, History, RefreshCw, ShieldAlert } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { DataTable } from '@/components/data-table/DataTable';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { getApiCall } from '@/service/httpCall';
import { API_ACL_SETTING_HISTORY } from '@/service/url';
import { ROUTES, USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { CalendarDateInput } from '@/components/ui/calendar-inputs';
import {
  MAX_RANGE_MONTHS,
  firstOfThisMonth,
  formatAuditDateTime,
  maxEndFor,
  minStartFor,
  toInputDate,
} from './auditDateRange';

const PAGE_SIZE = 100;

const ROLE_LABELS = {
  [USER_ROLES.SUPERADMIN]: 'Super Admin',
  [USER_ROLES.SUB_SUPERADMIN]: 'Sub Super Admin',
  [USER_ROLES.RESELLER]: 'Reseller',
  [USER_ROLES.COMPANY_ADMIN]: 'Company Admin',
  [USER_ROLES.CALL_CENTRE_ADMIN]: 'Call Center Admin',
  [USER_ROLES.CALL_CENTRE_TEAM_LEAD]: 'Team Lead',
  [USER_ROLES.CALL_CENTRE_AGENT]: 'Agent',
};

const roleLabel = (role: any) => ROLE_LABELS[role] || role || '—';

const formatDateTime = formatAuditDateTime;

/** "module_operations.Users.read" -> "Users › read" for readable diff paths. */
const prettyPath = (path: any) =>
  String(path ?? '')
    .split('.')
    .filter((part) => part && part !== '(root)')
    .join(' › ');

const prettyValue = (value: any) => {
  if (value === true) return 'Allowed';
  if (value === false) return 'Not allowed';
  if (value === null || value === undefined || value === '') return 'Empty';
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

/** Ops turned on for a module, used when a whole module is added / removed. */
const enabledOps = (moduleValue: any) => {
  if (!moduleValue || typeof moduleValue !== 'object') return [];
  return Object.keys(moduleValue).filter((key) => moduleValue[key] === true);
};

/** One-line summary of a history row for the table. */
const summarize = (row: any) => {
  const changes = row?.changes || {};
  const parts: string[] = [];

  if (row?.action === 'create') parts.push('ACL created');
  if (row?.action === 'clone') parts.push('ACL copied from another ACL');
  if (changes.acl_name) parts.push('Renamed');
  if (changes.default_for_role) parts.push('Default role changed');

  const ops = changes.module_operations;
  if (ops) {
    const added = ops.modules_added?.length || 0;
    const removed = ops.modules_removed?.length || 0;
    const changed = Object.keys(ops.modules_changed || {}).length;
    if (added) parts.push(`${added} module${added > 1 ? 's' : ''} added`);
    if (removed) parts.push(`${removed} module${removed > 1 ? 's' : ''} removed`);
    if (changed) parts.push(`${changed} module${changed > 1 ? 's' : ''} updated`);
  }

  const cascaded = Math.max((row?.affected_acl_ids?.length || 0) - 1, 0);
  if (cascaded > 0)
    parts.push(`same change applied to ${cascaded} linked ACL${cascaded > 1 ? 's' : ''}`);

  return parts.length ? parts.join(' · ') : 'No field changes recorded';
};

const ActionBadge = ({ action }: any) => {
  const tone =
    action === 'create'
      ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30'
      : action === 'delete'
        ? 'bg-rose-500/10 text-rose-500 border-rose-500/30'
        : 'bg-sky-500/10 text-sky-500 border-sky-500/30';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium capitalize ${tone}`}>
      {action || 'unknown'}
    </span>
  );
};

const FromTo = ({ from, to }: any) => (
  <span className="inline-flex flex-wrap items-center gap-1.5">
    <span className="rounded bg-muted px-1.5 py-0.5 text-xs text-muted-foreground line-through">
      {prettyValue(from)}
    </span>
    <ArrowRight className="h-3 w-3 text-muted-foreground" />
    <span className="rounded bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-foreground">
      {prettyValue(to)}
    </span>
  </span>
);

const Section = ({ title, children }: any) => (
  <div className="rounded-lg border border-border bg-card/40 p-3">
    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </div>
    {children}
  </div>
);

/** Full diff for one history entry. */
const ChangeDetails = ({ entry }: any) => {
  const changes = entry?.changes || {};
  const ops = changes.module_operations || {};
  const modulesChanged = Object.entries(ops.modules_changed || {});
  const hasAnything =
    changes.acl_name ||
    changes.default_for_role ||
    ops.modules_added?.length ||
    ops.modules_removed?.length ||
    modulesChanged.length;

  return (
    <div className="space-y-3">
      <div className="grid gap-3 sm:grid-cols-2">
        <Section title="When">
          <div className="text-sm text-foreground">{formatDateTime(entry?.created_at)}</div>
        </Section>
        <Section title="Changed by">
          <div className="text-sm text-foreground">
            {entry?.performedBy?.username || entry?.performedBy?.name || `User #${entry?.user_id}`}
          </div>
          <div className="text-xs text-muted-foreground">
            {roleLabel(entry?.performedBy?.role)}
            {entry?.performedBy?.email ? ` · ${entry.performedBy.email}` : ''}
          </div>
        </Section>
      </div>

      {changes.acl_name ? (
        <Section title="ACL name">
          <FromTo from={changes.acl_name.from} to={changes.acl_name.to} />
        </Section>
      ) : null}

      {changes.default_for_role ? (
        <Section title="Default for role">
          <FromTo
            from={changes.default_for_role.from ? roleLabel(changes.default_for_role.from) : null}
            to={changes.default_for_role.to ? roleLabel(changes.default_for_role.to) : null}
          />
        </Section>
      ) : null}

      {ops.modules_added?.length ? (
        <Section title={`Modules added (${ops.modules_added.length})`}>
          <div className="space-y-1.5">
            {ops.modules_added.map((item: any) => (
              <div key={`add-${item.module}`} className="text-sm">
                <span className="font-medium text-foreground">{item.module}</span>
                <span className="text-muted-foreground">
                  {' — '}
                  {enabledOps(item.value).length
                    ? `allowed: ${enabledOps(item.value).join(', ')}`
                    : 'no permission allowed'}
                </span>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {ops.modules_removed?.length ? (
        <Section title={`Modules removed (${ops.modules_removed.length})`}>
          <div className="space-y-1.5">
            {ops.modules_removed.map((item: any) => (
              <div key={`rem-${item.module}`} className="text-sm font-medium text-foreground">
                {item.module}
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {modulesChanged.length ? (
        <Section title={`Modules updated (${modulesChanged.length})`}>
          <div className="space-y-3">
            {modulesChanged.map(([moduleName, diffs]: any) => (
              <div key={`chg-${moduleName}`}>
                <div className="mb-1 text-sm font-medium text-foreground">{moduleName}</div>
                <div className="space-y-1 pl-3">
                  {(diffs || []).map((diff: any, idx: number) => (
                    <div key={`${moduleName}-${idx}`} className="flex flex-wrap items-center gap-2 text-xs">
                      <span className="text-muted-foreground">{prettyPath(diff.path) || 'value'}</span>
                      {diff.type === 'array_changed' ? (
                        <span className="text-foreground">
                          {diff.added?.length ? `added: ${prettyValue(diff.added)}` : ''}
                          {diff.added?.length && diff.removed?.length ? ' · ' : ''}
                          {diff.removed?.length ? `removed: ${prettyValue(diff.removed)}` : ''}
                        </span>
                      ) : diff.type === 'added' ? (
                        <span className="text-emerald-500">set to {prettyValue(diff.to)}</span>
                      ) : diff.type === 'removed' ? (
                        <span className="text-rose-500">removed ({prettyValue(diff.from)})</span>
                      ) : (
                        <FromTo from={diff.from} to={diff.to} />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      ) : null}

      {entry?.affected_acl_ids?.length > 1 ? (
        <Section title="Same change also applied to">
          <div className="text-sm text-foreground">
            {entry.affected_acl_ids.filter((id: any) => String(id) !== String(entry.acl_id)).length}{' '}
            linked ACL(s) that inherit from this one — ACL ID{' '}
            {entry.affected_acl_ids.filter((id: any) => String(id) !== String(entry.acl_id)).join(', ')}
          </div>
        </Section>
      ) : null}

      {!hasAnything ? (
        <div className="rounded-lg border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
          No field-level changes were recorded for this entry.
        </div>
      ) : null}
    </div>
  );
};

const AclHistoryPage = () => {
  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  const [startDate, setStartDate] = useState(() => toInputDate(firstOfThisMonth()));
  const [endDate, setEndDate] = useState(() => toInputDate(new Date()));
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<any>(null);

  const fetchHistory = async (offset = 0, range = { startDate, endDate }) => {
    if (range.startDate > range.endDate) {
      toast.error('From date cannot be after To date.');
      return;
    }
    if (range.endDate > maxEndFor(range.startDate)) {
      toast.error(`Date range cannot be longer than ${MAX_RANGE_MONTHS} months.`);
      return;
    }
    setLoading(true);
    try {
      const resp = await getApiCall(
        `${API_ACL_SETTING_HISTORY}?limit=${PAGE_SIZE}&offset=${offset}` +
          `&start_date=${range.startDate}&end_date=${range.endDate}`,
        {},
      );
      const list = Array.isArray(resp?.history) ? resp.history : [];
      setRows((prev) => (offset === 0 ? list : [...prev, ...list]));
      setTotal(Number(resp?.total) || 0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isSuperAdmin) {
      setLoading(false);
      return;
    }
    fetchHistory(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperAdmin]);

  const columns = useMemo<ColumnDef<any>[]>(
    () => [
      {
        accessorKey: 'created_at',
        header: 'When',
        cell: ({ row }: any) => (
          <span className="whitespace-nowrap text-sm">{formatDateTime(row.original.created_at)}</span>
        ),
      },
      {
        id: 'acl',
        header: 'ACL',
        accessorFn: (row: any) => row?.aclSetting?.acl_name || `#${row?.acl_id}`,
        cell: ({ row }: any) => {
          const acl = row.original.aclSetting;
          return (
            <div className="min-w-0">
              {acl?.id ? (
                <Link
                  to={`/${ROUTES.ACL_SETTING.EDIT.replace(':id', acl.id)}`}
                  className="truncate font-medium text-primary hover:underline"
                >
                  {acl.acl_name}
                </Link>
              ) : (
                <span className="font-medium text-muted-foreground">
                  ACL #{row.original.acl_id} (deleted)
                </span>
              )}
              {acl?.default_for_role ? (
                <div className="text-xs text-muted-foreground">
                  Default · {roleLabel(acl.default_for_role)}
                </div>
              ) : null}
            </div>
          );
        },
      },
      {
        accessorKey: 'action',
        header: 'Action',
        cell: ({ row }: any) => <ActionBadge action={row.original.action} />,
      },
      {
        id: 'performed_by',
        header: 'Changed by',
        accessorFn: (row: any) => row?.performedBy?.username || String(row?.user_id ?? ''),
        cell: ({ row }: any) => {
          const by = row.original.performedBy;
          return (
            <div className="min-w-0">
              <div className="truncate text-sm text-foreground">
                {by?.username || by?.name || `User #${row.original.user_id}`}
              </div>
              <div className="text-xs text-muted-foreground">{roleLabel(by?.role)}</div>
            </div>
          );
        },
      },
      {
        id: 'summary',
        header: 'What changed',
        accessorFn: (row: any) => summarize(row),
        cell: ({ row }: any) => (
          <span className="text-sm text-muted-foreground">{summarize(row.original)}</span>
        ),
      },
      {
        id: 'actions',
        header: '',
        enableSorting: false,
        cell: ({ row }: any) => (
          <Button variant="ghost" size="sm" onClick={() => setSelected(row.original)}>
            <Eye className="mr-1 h-4 w-4" />
            Details
          </Button>
        ),
      },
    ],
    [],
  );

  if (!isSuperAdmin) {
    return (
      <FillPage>
        <PageHeader title="ACL History" description="Super Admin only." />
        <div className="flex flex-1 items-center justify-center">
          <div className="flex max-w-md flex-col items-center gap-2 text-center">
            <ShieldAlert className="h-8 w-8 text-muted-foreground" />
            <div className="text-sm text-muted-foreground">
              Only a Super Admin can view ACL history.
            </div>
          </div>
        </div>
      </FillPage>
    );
  }

  return (
    <FillPage>
      <PageHeader
        compact
        title="ACL History"
        description={`Every ACL create and edit, newest first${total ? ` · ${total} entries` : ''} · up to ${MAX_RANGE_MONTHS} months per view`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1.5">
              <span className="text-xs text-muted-foreground">From</span>
              <CalendarDateInput
                value={startDate}
                onChange={(e) => {
                  const next = e;
                  if (!next) return;
                  setStartDate(next);
                  fetchHistory(0, { startDate: next, endDate });
                }}
                min={minStartFor(endDate)}
                max={endDate}
                className="h-8 w-[9.5rem]"
              />
              <span className="text-xs text-muted-foreground">To</span>
              <CalendarDateInput
                value={endDate}
                onChange={(e) => {
                  const next = e;
                  if (!next) return;
                  setEndDate(next);
                  fetchHistory(0, { startDate, endDate: next });
                }}
                min={startDate}
                max={maxEndFor(startDate)}
                className="h-8 w-[9.5rem]"
              />
            </div>
            {rows.length > 0 && rows.length < total ? (
              <Button variant="outline" size="sm" onClick={() => fetchHistory(rows.length)} disabled={loading}>
                <History className="mr-1 h-4 w-4" />
                Load older
              </Button>
            ) : null}
            <Button variant="outline" size="sm" onClick={() => fetchHistory(0)} disabled={loading}>
              <RefreshCw className="mr-1 h-4 w-4" />
              Refresh
            </Button>
          </div>
        }
      />

      <DataTable
        tableId="acl-setting.history:v1"
        columns={columns}
        data={rows}
        isLoading={loading}
        searchPlaceholder="Search by ACL, user or change…"
        emptyTitle="No ACL history"
        emptyDescription="No ACL was created or edited yet."
        getRowId={(row: any) => String(row.id)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename={`acl-history-${startDate}-to-${endDate}.csv`}
        pageSize={20}
      />

      <Dialog open={selected !== null} onOpenChange={(open) => !open && setSelected(null)}>
        <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selected?.aclSetting?.acl_name || `ACL #${selected?.acl_id}`} ·{' '}
              <span className="capitalize">{selected?.action}</span>
            </DialogTitle>
          </DialogHeader>
          {selected ? <ChangeDetails entry={selected} /> : null}
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default AclHistoryPage;
